import {
  Badge,
  Box,
  Collapse,
  HStack,
  Progress,
  Skeleton,
  Tag,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';

import type { AdjacentRoleWithSalary } from '../hooks/useAdjacentRoleSalaries';
import type { AdjacentRoleSkillData } from '../hooks/useAdjacentRoleSkills';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { UpliftMode } from '../IndividualPotentialPage';

const SalaryDisplay = ({
  targetSalary,
  salaryDeltaPercent,
  isSalaryLoading,
  formatCurrency,
}: {
  targetSalary: number | null;
  salaryDeltaPercent: number | null;
  isSalaryLoading: boolean;
  formatCurrency: (value: number) => string;
}) => {
  if (targetSalary !== null) {
    return (
      <>
        <Text fontWeight="medium" fontSize="sm">
          {formatCurrency(targetSalary)}
        </Text>
        {salaryDeltaPercent !== null && (
          <Badge
            colorScheme={salaryDeltaPercent >= 0 ? 'green' : 'red'}
            fontSize="xs"
            variant="subtle"
          >
            {salaryDeltaPercent >= 0 ? '+' : ''}
            {salaryDeltaPercent.toFixed(0)}%
          </Badge>
        )}
      </>
    );
  }

  if (isSalaryLoading) {
    return <Skeleton height="16px" width="70px" />;
  }

  return (
    <Text fontSize="xs" color="gray.400">
      N/A
    </Text>
  );
};

/** Compute skill match % from skill data */
const getSkillMatchPercent = (
  skillData: AdjacentRoleSkillData | undefined
): number | null => {
  if (!skillData || skillData.topSkills.length === 0) {
    return null;
  }
  return Math.round(
    (skillData.sharedSkills.length / skillData.topSkills.length) * 100
  );
};

/** Badge colour based on match % */
const getMatchColorScheme = (matchPercent: number): string => {
  if (matchPercent >= 60) {
    return 'green';
  }
  if (matchPercent >= 30) {
    return 'yellow';
  }
  return 'red';
};

const SkillMatchBadge = ({
  skillData,
  isLoading,
}: {
  skillData: AdjacentRoleSkillData | undefined;
  isLoading: boolean;
}) => {
  if (isLoading && !skillData) {
    return <Skeleton height="18px" width="50px" borderRadius="full" />;
  }

  const matchPercent = getSkillMatchPercent(skillData);
  if (matchPercent === null) {
    return null;
  }

  return (
    <Tooltip
      label={`You have ${skillData?.sharedSkills.length} of the top ${skillData?.topSkills.length} skills for this role`}
      fontSize="xs"
      hasArrow
    >
      <Badge
        colorScheme={getMatchColorScheme(matchPercent)}
        fontSize="2xs"
        variant="subtle"
      >
        {matchPercent}% skill match
      </Badge>
    </Tooltip>
  );
};

const SkillComparisonDisplay = ({
  skillData,
  isLoading,
}: {
  skillData: AdjacentRoleSkillData | undefined;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return <Skeleton height="24px" borderRadius="md" mt={1.5} />;
  }

  if (!skillData || skillData.topSkills.length === 0) {
    return null;
  }

  const { skillGaps, sharedSkills } = skillData;

  return (
    <VStack
      align="stretch"
      spacing={2}
      mt={2}
      pt={2}
      borderTop="1px solid"
      borderColor="gray.200"
    >
      {/* Skills you need to develop */}
      {skillGaps.length > 0 && (
        <Box>
          <Text fontSize="2xs" color="orange.600" fontWeight="semibold" mb={1}>
            Skills you'd need to develop
          </Text>
          <Wrap spacing={1}>
            {skillGaps.slice(0, 5).map((skill) => (
              <WrapItem key={skill.name}>
                <Tooltip
                  label={`${skill.share.toFixed(0)}% of people in this role have this skill`}
                  fontSize="xs"
                  hasArrow
                >
                  <Tag
                    size="sm"
                    colorScheme="orange"
                    variant="subtle"
                    fontSize="2xs"
                  >
                    {skill.name}
                    <Text as="span" ml={1} opacity={0.7} fontSize="2xs">
                      · {skill.share.toFixed(0)}% of role
                    </Text>
                  </Tag>
                </Tooltip>
              </WrapItem>
            ))}
            {skillGaps.length > 5 && (
              <WrapItem>
                <Tag
                  size="sm"
                  variant="subtle"
                  colorScheme="gray"
                  fontSize="2xs"
                >
                  +{skillGaps.length - 5} more
                </Tag>
              </WrapItem>
            )}
          </Wrap>
        </Box>
      )}

      {/* Skills you already have */}
      {sharedSkills.length > 0 && (
        <Box>
          <Text fontSize="2xs" color="green.600" fontWeight="semibold" mb={1}>
            Skills you already have
          </Text>
          <Wrap spacing={1}>
            {sharedSkills.slice(0, 5).map((skill) => (
              <WrapItem key={skill.name}>
                <Tooltip
                  label={`${skill.share.toFixed(0)}% of people in this role have this skill — and so do you`}
                  fontSize="xs"
                  hasArrow
                >
                  <Tag
                    size="sm"
                    colorScheme="green"
                    variant="subtle"
                    fontSize="2xs"
                  >
                    {skill.name}
                    <Text as="span" ml={1} opacity={0.7} fontSize="2xs">
                      · {skill.share.toFixed(0)}% of role
                    </Text>
                  </Tag>
                </Tooltip>
              </WrapItem>
            ))}
            {sharedSkills.length > 5 && (
              <WrapItem>
                <Tag
                  size="sm"
                  variant="subtle"
                  colorScheme="gray"
                  fontSize="2xs"
                >
                  +{sharedSkills.length - 5} more
                </Tag>
              </WrapItem>
            )}
          </Wrap>
        </Box>
      )}
    </VStack>
  );
};

interface AdjacentRolesPanelProps {
  roles: AdjacentRoleWithSalary[];
  currentRoleName: string | null;
  isLoading: boolean;
  isSalaryLoading: boolean;
  salaryProgress: number;
  skillsByRole: Map<number, AdjacentRoleSkillData>;
  isSkillsLoading: boolean;
  skillProgress: number;
  upliftMode: UpliftMode;
  /** Currently selected role ID (lifted to parent for cross-panel communication) */
  selectedRoleId: number | null;
  /** Callback when user selects/deselects a role */
  onRoleSelect: (roleId: number | null) => void;
}

export const AdjacentRolesPanel = ({
  roles,
  currentRoleName,
  isLoading,
  isSalaryLoading,
  salaryProgress,
  skillsByRole,
  isSkillsLoading,
  skillProgress,
  selectedRoleId,
  onRoleSelect,
}: AdjacentRolesPanelProps) => {
  const formatCurrency = useCurrencyFormatter();

  const toggleExpand = (roleId: number) => {
    onRoleSelect(selectedRoleId === roleId ? null : roleId);
  };

  if (isLoading) {
    return (
      <VStack align="stretch" spacing={3}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Common Career Moves
        </Text>
        <Skeleton height="60px" borderRadius="md" />
        <Skeleton height="60px" borderRadius="md" />
        <Skeleton height="60px" borderRadius="md" />
      </VStack>
    );
  }

  if (roles.length === 0) {
    return (
      <VStack align="stretch" spacing={3}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Common Career Moves
        </Text>
        <Box bg="gray.50" p={4} borderRadius="md" textAlign="center">
          <Text color="gray.500" fontSize="sm">
            Not enough transition data available for this role.
          </Text>
        </Box>
      </VStack>
    );
  }

  const isEnriching = isSalaryLoading || isSkillsLoading;
  let combinedProgress = skillProgress;
  if (isSalaryLoading && isSkillsLoading) {
    combinedProgress = (salaryProgress + skillProgress) / 2;
  } else if (isSalaryLoading) {
    combinedProgress = salaryProgress;
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
        Common Career Moves
      </Text>

      {isEnriching && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Loading details... ({Math.round(combinedProgress * 100)}%)
          </Text>
          <Progress
            value={combinedProgress * 100}
            size="xs"
            colorScheme="blue"
            borderRadius="full"
          />
        </Box>
      )}

      {roles.map((role) => {
        const skillData = skillsByRole.get(role.id);
        const isExpanded = selectedRoleId === role.id;
        const hasSkillData = skillData && skillData.topSkills.length > 0;

        return (
          <Box
            key={role.id}
            bg={isExpanded ? 'purple.50' : 'gray.50'}
            p={3}
            borderRadius="md"
            border="2px solid"
            borderColor={isExpanded ? 'purple.200' : 'transparent'}
            _hover={{ bg: isExpanded ? 'purple.50' : 'gray.100' }}
            transition="all 0.15s"
            cursor="pointer"
            onClick={() => toggleExpand(role.id)}
          >
            <HStack justify="space-between" align="flex-start">
              <VStack align="start" spacing={0.5} flex="1" minW={0}>
                <HStack spacing={1}>
                  <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                    {role.longName || role.shortName}
                  </Text>
                  {hasSkillData && (
                    <Text fontSize="xs" color="gray.400">
                      {isExpanded ? '▾' : '▸'}
                    </Text>
                  )}
                </HStack>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500">
                    {(role.transitionShare * 100).toFixed(1)}% transition rate
                    from {currentRoleName ?? 'your role'}
                  </Text>
                </HStack>
                <SkillMatchBadge
                  skillData={skillData}
                  isLoading={isSkillsLoading}
                />
              </VStack>

              <VStack align="end" spacing={0} flexShrink={0}>
                <SalaryDisplay
                  targetSalary={role.targetSalary}
                  salaryDeltaPercent={role.salaryDeltaPercent}
                  isSalaryLoading={isSalaryLoading}
                  formatCurrency={formatCurrency}
                />
              </VStack>
            </HStack>

            <Collapse in={isExpanded} animateOpacity>
              <SkillComparisonDisplay
                skillData={skillData}
                isLoading={isSkillsLoading && !skillData}
              />
            </Collapse>
          </Box>
        );
      })}
    </VStack>
  );
};
